import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./auth/auth-context";
import { LoginPage } from "./auth/login-page";
import CargoTestScene from "./components/cargo-test-scene";
import { DeviceModelManager } from "./components/device-model-manager";
import {
  DeviceModelCatalogProvider,
  useDeviceModelCatalog
} from "./models/device-model-catalog-context";
import { ProjectListPage } from "./projects/project-list-page";
import { ProjectWorkspace } from "./projects/project-workspace";

const deviceManagerPath = "/device-models";
const projectListPath = "/projects";
const testScenePath = "/cargo-test-scene";

export default function App() {
  return (
    <AuthProvider>
      <ApplicationGate />
    </AuthProvider>
  );
}

function ApplicationGate() {
  const { status } = useAuth();
  const [pathname, setPathname] = useState(readPathname);

  useEffect(() => {
    const handlePopState = () => setPathname(readPathname());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (nextPath: string, replace = false) => {
    if (replace) {
      window.history.replaceState({}, "", nextPath);
    } else {
      window.history.pushState({}, "", nextPath);
    }
    setPathname(readPathname());
  };

  if (status === "loading") {
    return <div className="application-gate">正在验证访问权限...</div>;
  }
  if (status === "unauthenticated") {
    return (
      <LoginPage
        onAuthenticated={() => {
          const returnTo = new URLSearchParams(window.location.search).get("returnTo");
          navigate(isSafeReturnPath(returnTo) ? returnTo : projectListPath, true);
        }}
      />
    );
  }

  return (
    <DeviceModelCatalogProvider>
      <CargoApplication navigate={navigate} pathname={pathname} />
    </DeviceModelCatalogProvider>
  );
}

function CargoApplication({
  navigate,
  pathname
}: {
  navigate: (path: string, replace?: boolean) => void;
  pathname: string;
}) {
  const { error, models, refresh, status } = useDeviceModelCatalog();

  if (status === "loading") {
    return <div className="device-catalog-gate">正在读取设备模型目录...</div>;
  }
  if (status === "error") {
    return (
      <div className="device-catalog-gate" role="alert">
        <strong>设备模型目录加载失败</strong>
        <span>{error}</span>
        <button onClick={() => void refresh().catch(() => undefined)} type="button">
          重试
        </button>
      </div>
    );
  }

  if (pathname === testScenePath) {
    return (
      <main className="shell">
        <section className="viewport" aria-label="Experimental model alignment scene">
          <CargoTestScene />
        </section>
      </main>
    );
  }
  if (pathname === deviceManagerPath) {
    return (
      <DeviceModelManager
        models={models}
        onCatalogChange={refresh}
        onClose={() => navigate(projectListPath)}
      />
    );
  }

  const projectId = readProjectId(pathname);
  if (projectId) {
    return (
      <ProjectWorkspace
        deviceModels={models}
        onBack={() => navigate(projectListPath)}
        onManageDevices={() => navigate(deviceManagerPath)}
        projectId={projectId}
      />
    );
  }

  return (
    <ProjectListPage
      onManageDevices={() => navigate(deviceManagerPath)}
      onOpenProject={(id) => navigate(`${projectListPath}/${encodeURIComponent(id)}`)}
    />
  );
}

function readPathname() {
  return window.location.pathname.replace(/\/$/, "") || "/";
}

function readProjectId(pathname: string) {
  const match = /^\/projects\/([^/]+)$/.exec(pathname);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function isSafeReturnPath(value: string | null): value is string {
  return Boolean(value?.startsWith("/") && !value.startsWith("//") && value !== "/login");
}
